'use client';
import { useTranslation } from 'react-i18next';
import { ArrowLeftFromLine, ArrowRightFromLine, Users } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipArrow } from '@/components/ui/tooltip';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsLoggedIn, useUserInfo } from '@/app/stores/useUserStore';
import { Sparkles } from 'lucide-react';
import { api, LiveRoom, UserFollow } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { getSidebarConfig, type SideBarItemType, type SideBarMenuItemInput } from '@/app/const/sidebar';
import { ChatUnreadBadge } from '@/components/chat-unread-badge';
import { UserAvatar } from '@/app/components/Lives/UserBase/avatar';
import { showLoginModal } from '@/components/login';
import { cn } from '@/lib/utils';


function LiveItem({
  item,
  isLeftSideOpen,
  size = 'sm',
  hoverabled = true,
}: {
  item: LiveRoom;
  isLeftSideOpen: boolean;
  size?: 'sm' | 'md';
  hoverabled?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-${size === 'sm' ? '2' : '3'} relative rounded-lg transition-all duration-300 px-2 min-h-[38px] ${
        hoverabled ? 'hover:bg-background-3/10 cursor-pointer ' : ''
      }`}
      onClick={() => {
        if (hoverabled) {
          if (item.status) {
            window.open(`/live/detail?id=${item.roomId}`, '_blank');
          } else {
            window.open(`/${item?.handle}`, '_blank');
          }
        }
      }}
    >
      <UserAvatar size={size === 'sm' ? 6 : 10} userInfo={item as unknown as UserFollow} forbidLink={true} hideLiveStatus={hoverabled} />
      {isLeftSideOpen && (
        <div className="flex flex-col">
          <span className={`text-${size === 'sm' ? 'xs' : 'sm'} text-font-primary ${size === 'sm' ? 'line-clamp-1' : 'line-clamp-3'} break-all`}>
            {item?.userName}
          </span>
          <span className={`text-${size === 'sm' ? 'xs' : 'sm'} text-font-primary opacity-50 ${size === 'sm' ? 'line-clamp-1' : 'line-clamp-2'}`}>
            {item?.roomName}
          </span>
        </div>
      )}
    </div>
  );
}
// 根据当前路径计算侧边栏默认是否展开（与 App getLayoutProps 逻辑一致）
function getDefaultOpenByPath(pathname: string): boolean {
  if (pathname.startsWith('/perps') || pathname.startsWith('/scan')) return false;
  return true;
}

export function MainSidebar({ currentPath, open = true, defaultOpen }: { currentPath?: string; open?: boolean; defaultOpen?: boolean }) {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const isLoggedIn = useIsLoggedIn();
  const userInfo = useUserInfo();
  const defaultOpenByPath = defaultOpen ?? getDefaultOpenByPath(pathname);
  // 新个人主页路由为 `/@:username/...`，侧边栏高亮仍归类到 `me`
  const normalizedCurrentPath = currentPath === userInfo?.username ? 'me' : currentPath;
  // 用于跟踪正在抖动的选项
  const [shakingItem, setShakingItem] = useState<string | null>(null);
  // 从localStorage读取缓存状态，如果没有则使用路径对应的默认值
  const [isLeftSideOpen, setIsLeftSideOpen] = useState(() => {
    if (!defaultOpenByPath) {
      return false;
    }
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem('billion-sidebar');
      return cached ? cached === 'true' : open;
    }
    return open;
  });
  const navigate = useNavigate();
  // 路径变化时同步侧边栏展开状态
  useEffect(() => {
    if (!defaultOpenByPath) {
      setIsLeftSideOpen(false);
    } else if (typeof window !== 'undefined') {
      const cached = localStorage.getItem('billion-sidebar');
      setIsLeftSideOpen(cached ? cached === 'true' : open);
    } else {
      setIsLeftSideOpen(open);
    }
  }, [pathname, defaultOpenByPath, open]);

  const [recommendLive, setRecommendLive] = useState<LiveRoom[]>([]);
  // 切换侧边栏状态并保存到localStorage
  const toggleSidebar = () => {
    const newState = !isLeftSideOpen;
    setIsLeftSideOpen(newState);
    if (typeof window !== 'undefined') {
      localStorage.setItem('billion-sidebar', newState.toString());
    }
  };
  // 使用公共配置获取侧边栏菜单项
  const sideBarItem: SideBarItemType = getSidebarConfig(t, isLoggedIn, userInfo);

  const handleTabChange = (item: SideBarMenuItemInput) => {
    // 判断是否需要登录
    // 如果需要登录的话，则出现登录弹窗
    if (item.needLogin && !isLoggedIn) {
      showLoginModal({ nextUrl: `${pathname}${search}` });
      return;
    }
    // 如果isSoon为true，则显示提示并触发抖动动画
    if (item.isSoon) {
      toast({
        title: t('action.tips'),
        description: t('action.featureComingSoon'),
      });
      // 触发抖动动画
      setShakingItem(item.path);
      // 动画持续500ms后清除状态
      setTimeout(() => {
        setShakingItem(null);
      }, 500);
      return;
    }
    // 跳转tab
    let newTab: string = item.path;
    // 资产中心
    if (newTab === 'portfolio') {
      navigate('/portfolio/spot');
      return;
    }
    // 直播管理
    if (newTab === 'admin') {
      navigate('/livecenter/shop');
      return;
    }
    // 关注
    if (newTab === 'follow') {
      navigate(`/${userInfo?.username}/follow`);
      return;
    }
    // 发现页
    if (newTab === 'discover') {
      navigate('/discover');
      return;
    }
    // 个人中心
    if (newTab === 'me') {
      navigate(`/${userInfo?.username}`);
      return;
    }
    // 直播间内部跳转
    navigate(`/${newTab}`);
  };
  // 获取推荐直播
  const getRecommendLive = async () => {
    const res = await api.room.getIndexBannerRoomList({ limit: 20 });
    // 截取第1-7个在播直播间
    const recommendLive = (res.data || []).filter(item => !!item.status).slice(0, 7);
    // 将数据存储在全局，方便下次打开
    setRecommendLive(recommendLive);
  };
  useEffect(() => {
    if (userInfo?.userId) {
      getRecommendLive();
    }
  }, [userInfo?.userId]);
  return (
    <div
      className={`hidden md:block z-[1001] co-left-side transition-all border-r border-button-border/75 dark:border-button-border/0 duration-100 ${isLeftSideOpen ? 'w-[180px] 2xl:w-[210px]' : 'w-[72px]'}`}
    >
      <div className="co-left-side-main bg-background">
        <div className="co-left-navigation px-4 py-2 flex flex-col gap-1">
          {sideBarItem.map((item, index) => (
            <Fragment key={index}>
              {item.map(i =>
                i?.hidden ? null : isLeftSideOpen ? (
                  <div
                    key={i.path}
                    className={cn(
                      'cursor-pointer flex items-center gap-2 hover:bg-background-3/10 rounded-lg px-2.5 h-[36px]  relative transition-transform',
                      i.paths?.includes(normalizedCurrentPath || '') ? 'bg-background-3/10 text-font-primary' : 'text-font-secondary',
                      shakingItem === i.path ? 'animate-shake' : ''
                    )}
                    onClick={() => {
                      handleTabChange(i);
                    }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center mr-1">
                      {i.paths?.includes(normalizedCurrentPath || '') && i.activeIcon ? (
                        <i.activeIcon className={`flex-none ${i.size ? i.size : 'size-[18px]'}`} />
                      ) : (
                        <i.icon
                          className={`flex-none ${i.size ? i.size : 'size-[18px]'}`}
                          fill={i.paths?.includes(normalizedCurrentPath || '') ? 'currentColor' : 'none'}
                        />
                      )}
                    </div>

                    <div className={`text-sm ${isLeftSideOpen ? 'block' : 'hidden'}`}>{i.text}</div>
                    {i.showUnRead ? (
                      <ChatUnreadBadge 
                        expanded={true} 
                        className="absolute top-1/2 -translate-y-1/2 right-2 text-white"
                      />
                    ) : (
                      (i.isNew || i.isSoon) && (
                        <Badge
                          variant={i.isSoon ? 'green' : 'danger'}
                          size="xs"
                          className="rounded-full rounded-bl-none absolute top-1/2 -translate-y-1/2 right-2 scale-75 text-white"
                        >
                          {i.isSoon ? 'Soon' : 'New'}
                        </Badge>
                      )
                    )}
                  </div>
                ) : (
                  <TooltipProvider delayDuration={0} key={i.path}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={`cursor-pointer flex items-center gap-2 hover:bg-background-3/10 rounded-lg px-2.5 h-[36px] relative transition-transform ${
                            i.paths?.includes(normalizedCurrentPath || '') ? 'bg-background-3/10' : ''
                          } ${shakingItem === i.path ? 'animate-shake' : ''}`}
                          onClick={() => {
                            handleTabChange(i);
                          }}
                        >
                          <div className="w-5 h-5 flex items-center justify-center">
                            {i.paths?.includes(normalizedCurrentPath || '') && i.activeIcon ? (
                              <i.activeIcon className={`flex-none ${i.size ? i.size : 'size-[18px]'}`} />
                            ) : (
                              <i.icon
                                className={`flex-none ${i.size ? i.size : 'size-[18px]'}`}
                                fill={i.paths?.includes(normalizedCurrentPath || '') ? 'currentColor' : 'none'}
                              />
                            )}
                          </div>
                          {i.showUnRead ? (
                            <ChatUnreadBadge 
                              expanded={false} 
                              collapsedClassName="absolute scale-75 top-0.5 left-4 text-white"
                            />
                          ) : (
                            (i.isNew || i.isSoon) && (
                              <Badge
                                variant={i.isSoon ? 'green' : 'danger'}
                                size="xs"
                                className="rounded-full rounded-bl-none absolute scale-75 top-0.5 left-4 text-white"
                              >
                                {i.isSoon ? 'Soon' : 'New'}
                              </Badge>
                            )
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={5} className="z-[1000]">
                        <TooltipArrow />
                        <span className="text-sm text-font-primary">{i.text}</span>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )
              )}
              {index !== sideBarItem.length - 1 && <div className="h-[0.5px] bg-button-border/75 -mx-1 my-1" />}
            </Fragment>
          ))}
          {recommendLive.length > 0 && (
            <>
              <div className="h-[0.5px] bg-button-border/75 -mx-1 my-1"></div>
              <div className="flex flex-col gap-1 pb-1">
                <div className="flex items-center gap-2 h-[40px] px-3">
                  {isLeftSideOpen ? (
                    <Sparkles className="mr-1 size-[18px] flex-none" />
                  ) : (
                    <Sparkles className="mr-1 size-[18px] flex-none" fill="currentColor" />
                  )}
                  {isLeftSideOpen && (
                    <div className={`text-sm text-font-primary ${isLeftSideOpen ? 'block' : 'hidden'}`}>{t('post.featuredPosts')}</div>
                  )}
                </div>
                {recommendLive.map((item, index) => (
                  <TooltipProvider delayDuration={0} key={index}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="w-full">
                          <LiveItem item={item} isLeftSideOpen={isLeftSideOpen} />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" sideOffset={5}>
                        <TooltipArrow />
                        <div className="w-[300px] py-3 flex items-center justify-between">
                          <LiveItem item={item} isLeftSideOpen={true} size="md" hoverabled={false} />
                          <span className="text-sm text-font-primary flex items-center gap-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>
                            <Users className="w-4 h-4" fill="currentColor" />
                            {item.roomUserCount}
                          </span>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            </>
          )}
        </div>
        {/* <div className='co-left-navigation-bottom'>
                    113213123
                </div> */}
      </div>
      <div className="co-left-bottom bg-background">
        <div
          className="cursor-pointer w-8 h-8 mx-auto flex items-center justify-center rounded-lg bg-background-3/10 hover:bg-background-3/20 transition-all duration-300"
          onClick={toggleSidebar}
        >
          {isLeftSideOpen ? <ArrowLeftFromLine className="w-4 h-4" /> : <ArrowRightFromLine className="w-4 h-4" />}
        </div>
      </div>
    </div>
  );
}
